/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Plus, Star } from 'lucide-react';
import { Link, NavLink, Outlet, useLocation, useMatch } from 'react-router-dom';
import { preloadViews } from './lazyViews';
import { activeCircleLabel, useCircles } from '@/queries/session';
import { cn } from '@/lib/cn';
import { VIEW_ICONS } from './ViewIcons';
import { CircleSwitcher } from '@/board/CircleSwitcher';
import { BoardSearch } from '@/board/BoardSearch';
import { BoardControls } from '@/board/BoardControls';
import { ActiveFilterChips } from '@/board/ActiveFilterChips';
import { StatusBanner } from './StatusBanner';
import { NoCircle } from './NoCircle';
import { AccountMenu } from './AccountMenu';
import { Notifications } from './Notifications';
import { MyDayPromo } from './MyDayPromo';
import { PushProvider } from './PushProvider';
import { Tip } from './Tip';

/**
 * The shell every view sits inside: the topbar, then the view.
 *
 * **The ids and data attributes here are a contract, not decoration.** The
 * browser tests select `[data-view="board"]`, `#board-title`, `#view-container`
 * and friends, and they are the acceptance gate for this migration — they assert
 * behaviour rather than implementation, which is why they survive a rewrite at
 * all. Renaming one of these is a deliberate decision plus a test edit; see
 * docs/react-migration/plan.md §8.
 *
 * P0 builds the shell only. The view switch routes, the heading names the circle,
 * and everything else is a placeholder until P1.
 */

/**
 * The switch holds four things, and they are four of the same kind of thing.
 *
 * **My Day used to be the fourth of five here, and it did not belong.** Board,
 * List and Calendar are three renderings of *the same work*; Archived is the
 * same board over a different time window. My Day is not a view of this board at
 * all — it is your own ticked items across every circle you are in, and it
 * ignores the circle switcher sitting next to it. Putting it in a row of view
 * tabs said it was a fourth way of looking at this board, which it is not, and
 * it cost the row about 90px to say so.
 *
 * It is now a button of its own, up with the bell and the account — the other
 * two controls on this bar that are about *you* rather than about the board.
 * See `PlanMyDay` below. The route is unchanged, and so is `data-view="todo"`,
 * which is what twelve assertions reach for.
 */
const VIEWS = [
  { view: 'board', to: '/board', label: 'Board', word: 'view' },
  { view: 'list', to: '/list', label: 'List', word: 'view' },
  { view: 'calendar', to: '/calendar', label: 'Calendar', word: 'view' },
  { view: 'archived', to: '/archived', label: 'Archived', word: null },
] as const;

export function AppLayout() {
  const { data: circles } = useCircles();
  // The search, the count and Filters belong to the board and appear only there.
  // Offering a board filter on My Day would be a control that cannot do anything.
  //
  // Both matches are read into variables before being combined: `a() || b()`
  // short-circuits, which would skip the second hook on the turns where the first
  // matched — a conditional hook call, and the kind that only misbehaves on the
  // render where the route changes.
  const exact = useMatch('/board');
  const nested = useMatch('/board/*');
  const onBoard = exact !== null || nested !== null;
  // My Day is the one view the board's filters do not reach — it lists your own
  // ticked items across circles, so a vertical or a tag filter would narrow
  // nothing. Hidden there and offered everywhere else, which is the line
  // `.topbar.todo-mode .filter-wrap{display:none}` draws in the vanilla app.
  const onMyDay = useMatch('/my-day') !== null;
  // The List and Archived views draw the same three filters as inline selects in
  // their own toolbars. Naming them a second time on the bar above would be two
  // controls for one state, so the chip row is for the views where the filters
  // are only reachable through the popover — the board and the calendar.
  //
  // **Read into variables first, for the reason spelled out immediately above.**
  // This was written as `useMatch('/list') !== null || useMatch('/archived') !== null`,
  // which short-circuits: on the List view the first half is true, the second
  // `useMatch` is never called, the hook count changes between renders, and React
  // throws "Should have a queue. You are likely calling Hooks conditionally."
  // Three lines under a comment warning about exactly this.
  const onList = useMatch('/list');
  const onArchived = useMatch('/archived');
  const filtersAreInline = onList !== null || onArchived !== null;

  // The search and the filters live in the query string, so a bare `to="/list"`
  // would drop them at the door — somebody who searched on the board and then
  // switched views would watch their term vanish. The vanilla app kept those in
  // module variables, which survived a view change for free; putting them in the
  // URL is what buys a shareable link, and carrying the search across is the
  // price of it. `board-search` asserts the term is still there both ways.
  const { search } = useLocation();

  // Owned here, not inside the search, because opening it changes what else the
  // row shows.
  const [searchOpen, setSearchOpen] = useState(false);

  // Fetch the other four views while the browser is idle, so that splitting them
  // out costs the first paint and nothing after it. Runs once per session — the
  // browser caches the chunks, and the import promises are memoised anyway.
  useEffect(preloadViews, []);

  const active = circles?.circles.find((c) => c.id === circles.activeId);
  // Two states now that the combined board is gone: the circle you are on, or
  // the app's own name while circles are still loading and for somebody with
  // none. One helper, so the heading and the switcher can never disagree.
  const heading = activeCircleLabel(circles);
  // Somebody in no circle at all. `circles` being undefined means it has not
  // loaded yet, which is not the same thing and must not hide the button
  // permanently after a slow first request.
  const hasCircle = circles === undefined || circles.circles.length > 0;

  return (
    // **Inside the layout, so it is inside the session.** Everything push does
    // — registering a worker, asking permission, showing the one-time card —
    // belongs behind a signed-in board and must never touch the sign-in screen.
    <PushProvider>
    <div className="min-h-dvh">
      {/* **The bar is two rows, and the split is what makes the second one
          affordable.**

          Everything used to compete on one line — mark, ring, circle name,
          search, New task, the five-view switch, the tally, Filters, the bell
          and the avatar — and the row could not pay for all of it. The mark had
          to be cropped to its glyph and then hidden below `phone`, the search
          gave up 40px, and the circle's name was still the thing that absorbed
          every new arrival, down to 3px of "Core" at 1440.

          Splitting by *what the controls belong to* rather than by what fits
          settles that. Row one is the product and the person: the wordmark, the
          bell, the account. Neither changes when you switch circle or view, and
          nothing on it competes for width — which is why the full 903x218
          wordmark fits here at every size, uncropped, when the old row could not
          take it at 1441.

          Row two is the board: which circle, its name, its search, New task, the
          view switch and the board's own tally and filters. It inherits a row
          with three fewer occupants than before.

          Full-bleed and near-white on the tinted page, separated by a border and
          not a shadow — `index.css` reserves shadow for "lifted", and this is
          chrome, not a card. Its own gradient and hairline are in `.topbar`
          there; the utilities that used to set them are gone, because a flat
          `bg-surface` cannot express a two-stop fade and a tinted rule at
          once. */}
      <header className="topbar">
        <div className="mx-auto max-w-[2200px] px-3 phone:px-[18px] tablet:px-8">
          {/* ------------------------------------------------ row one: identity */}
          <div className="topbar-identity flex h-[56px] items-center gap-3 tablet:h-[62px]">
            {/* The whole wordmark, mark and lettering both. It was cropped to a
                28px window on the old single row because `topbar-responsive`
                measured the 108px it costs coming straight out of the search box
                (down to 43px at 1441) and then out of the circle's name (5px at
                360). On a row whose only other occupants are two icons, it costs
                nothing anybody else needs. */}
            <Link
              to="/board"
              aria-label="Qollab — go to the board"
              className="brand-logo-wrap shrink-0 leading-none"
            >
              <img
                src="/qollab-logo.png"
                alt="Qollab"
                width={903}
                height={218}
                // 32/38, up from 28/32. The mark was sized when it shared a row
                // with nine other things and every pixel of it came out of the
                // circle's name; on a row holding two icons it is the only thing
                // with an opinion about height, and 62px of row was carrying a
                // 32px logo. Still short of the row — `topbar-responsive`
                // measures the bar's rectangles, and a mark that sets the row's
                // height starts deciding the layout rather than sitting in it.
                className="block h-[32px] w-auto tablet:h-[38px]"
              />
            </Link>

            {/* Always present, on every view: the feed is about work anywhere, and
                the menu is how somebody signs out and how an administrator reaches
                the two screens that are not views.

                `topbar-actions` is a name two tests hold — both assert this group
                never wraps. It is down to the bell and the avatar now; the tally
                and Filters moved to the row with the board they describe. */}
            <div className="topbar-actions ml-auto flex flex-nowrap items-center gap-2">
              {/* Yours, not the board's — which is exactly why it sits with the
                  bell and the account rather than in the view switch. */}
              {/* `onMyDay` is already computed above for the filters; passing it
                  down keeps one `useMatch` for one question, and is what this
                  control needs now that its className cannot be a function. */}
              <PlanMyDay search={search} isActive={onMyDay} />
              <Notifications />
              <AccountMenu />
              {/* Not a row control — it renders nothing here itself, only its
                  own portal, once a day. Mounted wherever it is easiest to find
                  again, since position on this bar has no bearing on where it
                  actually appears. */}
              <MyDayPromo />
            </div>
          </div>

          {/* ---------------------------------------------------- row two: board */}
          {/* **`1fr auto 1fr` — the switch is centred, and it is now nearly free.**

              Symmetric columns are what centre the switch: the group on each side
              gets an equal share, so the middle one lands on the row's midpoint.
              That was briefly abandoned, and the measurement that drove it was
              real — at 1440 the tools group wanted 123px, was handed 460, and the
              circle's name beside it was down to 81px of "New Relic Client
              Delivery". Symmetry was being paid for out of the one thing on the
              row that had something to say.

              What changed is not the argument, it is the arithmetic. The right
              column is no longer a tally and a Filters button; the search box and
              New task moved into it, so it wants about 450px of the 472 an equal
              share gives it at 1440. There is almost nothing left to waste, and
              the left column keeps enough for the whole circle name. The cost of
              centring went from 337px to roughly 20.

              Below `tablet` the switch drops to a row of its own and is centred
              there instead, which is the only place it fits. */}
          <div className="topbar-work grid grid-cols-[auto_1fr] items-center gap-3 border-t border-border/70 py-2.5 tablet:grid-cols-[1fr_auto_1fr] tablet:gap-4 tablet:py-3">
        {/* nowrap, deliberately: a wrapping flex container does not shrink an item
            that does not fit, it moves it to the next line at full size — which is
            how a circle called "New Relic Client Delivery" put this row on three
            lines of a phone. The heading clips instead; that is what its ellipsis
            and its title attribute are for. */}
        {/* `brand` is load-bearing, not decorative. `topbar-responsive` measures
            this group for wrapping and for items drawn on top of each other —
            and its probe returns 0 lines for a selector that matches nothing, so
            without this class two of its assertions pass by measuring an element
            that is not there. It was missing for four phases and the contract
            script reported it present, because the word appears in a comment.

            It now holds one thing: the heading, which is either the switcher
            pill or a plain name. The search and New task moved across to the
            tools group, which is where they read as instruments of the board
            rather than parts of its title. */}
        <div className="brand flex min-w-0 flex-nowrap items-center gap-2.5">
            {/* **The heading is still an `h1`; what changed is what it holds.**
                The circle's name used to be printed here beside a separate
                switcher. Now, for anybody who can switch, the whole heading *is*
                the switcher — swatch, name and chevron in one pressable pill —
                and `#board-title` is the name span inside it. A `<button>` may
                contain phrasing content and not a heading, which is why the `h1`
                wraps the button rather than the other way round.

                `canSwitch === true` rather than truthiness: while the session is
                still loading it is `undefined`, and rendering neither branch
                would leave the page briefly without a `#board-title` at all. */}
            {/* While the collapsed search is open on a narrow screen the heading
                steps aside for as long as you are typing, and comes back when
                you close it. Below `laptop` there is no room for both, and the
                alternative — wrapping — does not shrink anything, it just adds a
                line.

                This used to be a `display: contents` wrapper around the heading,
                from when the search and New task were in here too and had to
                stay siblings of it. With those gone the wrapper is not only
                unnecessary, it is harmful: an element with `display: contents`
                generates no box, so `robustness` — which counts the distinct
                vertical centres of `.brand`'s children to prove the group has
                not wrapped — found the group's only child unmeasurable and
                counted zero rows where it wanted one. */}
            <h1
              className={cn(
                'm-0 flex min-w-0 shrink items-center gap-2 text-xl font-semibold tracking-[-0.015em] full:text-2xl',
                searchOpen && 'hidden laptop:flex',
              )}
            >
              {circles?.canSwitch === true ? (
                <CircleSwitcher />
              ) : (
                <>
                  {/* The dot appears only for somebody who has no switcher to
                      look at. With a swatch in the pill the colour is already on
                      screen, and a second copy of it two centimetres away reads
                      as a different thing rather than the same one.
                      `board-header` asserts both halves: no dot when the
                      switcher is there, and a painted dot when it is not.

                      It is the same ring as the switcher's swatch, and its size
                      and shape come from `index.css` — so no `size-*` here, or
                      the utility would win and the one person without a switcher
                      would get a smaller dot than everybody else. `--dot` feeds
                      its glow; the colour stays on `background-color`, which is
                      what `board-header` reads. */}
                  {active && circles?.canSwitch === false && (
                    <span
                      className="board-title-dot shrink-0"
                      style={{ '--dot': active.color, borderColor: active.color } as React.CSSProperties}
                      aria-hidden
                    />
                  )}
                  <span id="board-title" title={heading} className="board-title-text truncate">
                    {heading}
                  </span>
                </>
              )}

            </h1>
        </div>

        <ViewSwitch search={search} />

        {/* The board's own instruments, on the board's own row. They read the
            board's data and they change with it — the tally counts what is
            showing, Filters lights up when one is on — so they belong here and
            not beside the account menu, which was only ever where there was
            space left.

            **The order is fixed by an assertion, not by taste.** `board-search`
            checks that New task sits within 24px of the search box and that the
            tally is on the far side of it, because both the tally and Clear
            change width as somebody types and either between them would shove
            the button about mid-search. So: box, then the button, then the
            things that resize.

            `w-full` with the items pushed right, rather than `justify-self-end`.
            The column is the one that has slack, and the collapsed search needs
            somewhere to expand into when it opens on a phone — a group sized to
            its own content has nothing to give it.

            Nothing here for somebody with no circle. Filters would filter an
            empty board, the count would count nothing, and New task would offer
            a save that can only fail; `access-view` and `robustness` both check
            that such a person is offered none of it. On the board or off it,
            though: the List view drives these same filters from its own toolbar.
            Only the tally is board-only, and BoardControls empties it rather
            than unmounting. */}
        <div className="board-tools flex w-full flex-nowrap items-center justify-end gap-2">
          {onBoard && hasCircle && <BoardSearch open={searchOpen} onOpenChange={setSearchOpen} />}

          {/* **Icon only, and therefore a real tooltip.** The label went because
              this is the one control on the bar whose meaning a plus sign
              carries completely — and because the words were 74px of the row
              that the circle's name kept paying for. `aria-label` is still the
              accessible name; `Tip` is the sighted person's. The native `title`
              is gone, or both would fire half a second apart. */}
          {hasCircle && (
            <Tip label="New task">
              <Link
                to="/board/task/new"
                aria-label="New task"
                // **The fill, the rim, the lift and the hover are all in
                // `index.css` under `.new-task-btn`** — not utilities here,
                // because a `bg-*` utility sets `background-color` and would
                // sit under a gradient's `background-image`.
                //
                // **There is no brand colour left on this bar.** This was the
                // last of it, and it now wears the same white chip as the view
                // switch, at Abhishek's ask. `index.css` records what that
                // costs: it reads as the fourth white bordered control up here
                // rather than as the one thing you can do. The dialogs' primary
                // button kept the blue, where Cancel sits next to it.
                className="new-task-btn tap flex size-[34px] shrink-0 items-center justify-center rounded-lg no-underline"
              >
                <Plus className="size-[18px] shrink-0" aria-hidden />
              </Link>
            </Tip>
          )}

          {hasCircle && <BoardControls onBoard={onBoard} hideFilters={onMyDay} />}
        </div>
          </div>

          {/* A third row, and only when it has something to say. It renders
              nothing at all when no filter is on, so the bar does not carry an
              empty strip around for a state that does not exist. */}
          {hasCircle && !onMyDay && !filtersAreInline && <ActiveFilterChips />}
        </div>
      </header>

      <div className="mx-auto max-w-[2200px] px-3 pb-10 pt-5 phone:px-[18px] tablet:px-8 tablet:pt-6">
        <div id="view-container">
          {/* One check for every route. Put inside BoardView it guarded the board
              alone, and the other four views each rendered their own empty state —
              which says "there is no work" to somebody whose situation is "you
              cannot see any work". `circles` being undefined means it has not
              loaded yet, which is not the same as having none. */}
          {circles && circles.circles.length === 0 ? (
            <NoCircle />
          ) : (
            // Four of the five views are fetched on demand. The fallback is an
            // empty box rather than a spinner, and it holds a height on purpose:
            // a fallback that collapses lets the page jump up and back down
            // again, which is more noticeable than the wait it is covering. In
            // practice it is rarely seen at all — `preloadViews` has usually
            // fetched the chunk while the board was idle.
            <Suspense fallback={<ViewSkeleton />}>
              <Outlet />
            </Suspense>
          )}
        </div>
      </div>

      {/* Outside #view-container deliberately: a message about a failed write
          must not be replaced by the very re-render the failure caused. */}
      <StatusBanner />
    </div>
    </PushProvider>
  );
}

/**
 * What a view looks like while its chunk is arriving.
 *
 * It was an empty box holding 60vh. That was right about the height — a fallback
 * that collapses lets the page jump up and back down again, which is more
 * noticeable than the wait it is covering — and wrong about everything else: a
 * blank rectangle reads as "there is nothing here", which is the same thing this
 * app says to somebody with no circle.
 *
 * Column-shaped, because four of the five views are columns or rows of cards,
 * and a shape that is roughly right is what makes a wait read as loading rather
 * than as breakage. It is rarely seen at all — `preloadViews` has usually
 * fetched the chunk while the board was idle — which is exactly why it must not
 * be alarming on the occasions it is.
 */
function ViewSkeleton() {
  return (
    <div
      className="grid min-h-[60vh] gap-3.5 tablet:grid-cols-[repeat(auto-fit,minmax(240px,1fr))]"
      aria-hidden
    >
      {[0, 1, 2, 3, 4].map((col) => (
        <div key={col} className="flex flex-col gap-2.5">
          <div className="h-4 w-24 animate-pulse rounded-full bg-black/[0.06]" />
          {[0, 1].map((card) => (
            <div
              key={card}
              className="h-24 animate-pulse rounded-card bg-black/[0.04]"
              // Staggered, so it reads as a list filling rather than as one
              // block flashing.
              style={{ animationDelay: `${(col * 2 + card) * 60}ms` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Where the chip is, in the switch's own coordinates. `on` is separate from the
 *  rest so leaving the switch entirely — My Day is not one of these views — fades
 *  it out **in place** rather than unmounting it, which would make coming back a
 *  jump from nowhere instead of a fade in at the tab you chose. */
type Thumb = { x: number; y: number; w: number; h: number; on: boolean };

/**
 * The view switch, and the one moving part on the bar.
 *
 * **The selected view is a chip that travels, not a background that teleports.**
 * `bg-ink` used to arrive on the tab you clicked and leave the one you left —
 * two instant repaints in opposite places, which the eye reads as a flicker
 * rather than as a selection moving. There is one filled element now; this
 * measures the active link and translates it there, and `.vs-thumb` in
 * `index.css` does the sliding.
 *
 * **Measured, not computed.** The obvious version keeps an index and multiplies
 * by a tab width, and it is wrong at every breakpoint: below `laptop` the
 * inactive tabs drop their labels and below `full` they drop the word "view", so
 * the four tabs are four different widths and the current one is the widest.
 * Reading `offsetLeft`/`offsetWidth` off the element that is actually active
 * costs one layout read per navigation and cannot disagree with what is drawn.
 *
 * The `ResizeObserver` is what covers the rest: crossing a breakpoint changes
 * the tabs' widths without any navigation, and a chip left behind at the old
 * arithmetic is worse than no chip. Every link is observed, not just the nav —
 * the nav is content-sized so its width usually changes too, but "usually" is
 * not a thing to hang this on.
 *
 * **The active label is `text-bg`, and the chip is the only reason it is
 * legible** — it is the page colour, printed on a track that is also the page
 * colour. That is safe because the measurement runs in `useLayoutEffect`, whose
 * state update React flushes before paint: there is no frame in which a tab is
 * marked active and the chip is not behind it. It is also why the chip is not
 * `bg-ink` on the link as well as here — a fill that appears instantly on the
 * new tab is precisely the repaint this replaces.
 */
function ViewSwitch({ search }: { search: string }) {
  const navRef = useRef<HTMLElement | null>(null);
  const [thumb, setThumb] = useState<Thumb | null>(null);
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const measure = () => {
      const el = nav.querySelector<HTMLElement>('a.active');
      setThumb((prev) => {
        const next: Thumb | null = el
          ? { x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight, on: true }
          : prev && { ...prev, on: false };
        // Returning `prev` unchanged is what stops the observer from re-rendering
        // on every scrollbar-driven reflow. Cheap, and it keeps this honest: the
        // only renders are the ones where the chip actually has somewhere to go.
        if (!next || !prev) return next;
        const same = next.x === prev.x && next.y === prev.y
          && next.w === prev.w && next.h === prev.h && next.on === prev.on;
        return same ? prev : next;
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(nav);
    nav.querySelectorAll('a').forEach((a) => ro.observe(a));
    return () => ro.disconnect();
  }, [pathname]);

  return (
    // A third row on a tablet and below; centred under the board's name.
    // The track is the page colour rather than white now that the bar it sits on
    // is white — a white pill on a white bar is a border holding five buttons,
    // and a tinted one is a segmented control.
    <nav
      ref={navRef}
      className="view-switch order-last col-span-2 flex justify-self-center rounded-[10px] border border-border bg-bg p-[3px] tablet:order-none tablet:col-span-1"
      aria-label="View"
    >
      {/* Rendered only once it has been measured, so it mounts already in the
          right place. Mounting it at zero and then moving it would animate a
          fly-in from the left edge on first paint — a transition only runs on a
          *change* of value, never on the first one. */}
      {thumb && (
        <span
          className="vs-thumb"
          style={{
            transform: `translate(${thumb.x}px, ${thumb.y}px)`,
            width: thumb.w,
            height: thumb.h,
            opacity: thumb.on ? 1 : 0,
          }}
          aria-hidden
        />
      )}

      {VIEWS.map((v) => (
        <NavLink
          key={v.view}
          to={{ pathname: v.to, search }}
          // **Cross-fade between views, with no library.** React Router 7
          // drives the browser's own View Transitions API from this prop.
          // Switching used to be an instant repaint — the one moment in the
          // app where a whole screen changes at once, and nothing
          // acknowledged it. The duration and the reduced-motion guard live
          // in index.css, next to every other piece of motion.
          viewTransition
          data-view={v.view}
          title={v.word ? `${v.label} ${v.word}` : v.label}
          className={({ isActive }) =>
            cn(
              'tap flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-[7px] text-sm font-semibold transition-colors duration-fast ease-out-soft',
              // `active` as well as the styling, because that is the name the
              // vanilla switch uses and `topbar-responsive` asks which view
              // you are on through it.
              // **`text-ink` against `text-ink-muted`, and that pair is now
              // load-bearing.** While the chip was a dark fill this was
              // `text-bg` — the label inverted because the thing under it did.
              // The chip is a subtle white now, 1.11:1 against the track, so it
              // is not carrying the selected state on its fill: the rim, the
              // lift, and this step from muted to full ink are, and weakening
              // any of the three leaves the switch saying nothing about where
              // you are.
              //
              // A *function* className is safe here and is not safe on
              // `PlanMyDay` below: these links are not wrapped in a `Tip`, and
              // it is the Radix trigger's prop merge that turns a function into
              // its own source code. See the note there.
              isActive ? 'active text-ink' : 'text-ink-muted hover:bg-surface hover:text-ink',
            )
          }
        >
          {({ isActive }) => (
            <>
              {VIEW_ICONS[v.view]}
              {/* Below `laptop` only the view you are on keeps its words; the
                  rest are their icon alone. Naming the current one is what
                  keeps the group readable — an all-icon switch makes you work
                  out where you are. The icon is why this is legible at all
                  once the label goes; without it these are blank buttons. */}
              <span className={cn('vs-label', !isActive && 'hidden laptop:inline')}>
                {v.label}
                {v.word && <span className="vs-word hidden full:inline"> {v.word}</span>}
              </span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * "My Day" — the personal list, promoted out of the view switch.
 *
 * **It keeps `data-view="todo"`.** That attribute is not styling: twelve
 * assertions across `my-day-view`, `pin-todo-view` and `access-view` reach the
 * list through `browserLib.view(page, 'todo')`, which clicks
 * `[data-view="todo"]` wherever it happens to be. Moving the control without
 * moving the handle is what makes this a rearrangement rather than a rewrite.
 *
 * **It said "Plan my day" until Abhishek asked for the name instead.** The verb
 * was chosen on the argument that a button is what you are *about to do* while
 * the screen is what you are *looking at* — which is a fine argument and still
 * left the product calling one place two things. `CLAUDE.md` is explicit that the
 * personal list is named My Day everywhere a person can read it; the top bar was
 * the last place that was not. One name, and the button now matches the heading
 * it takes you to.
 *
 * The words go below `phone`, where the row is a logo and three controls and
 * every one of them is already an icon.
 */
function PlanMyDay({ search, isActive }: { search: string; isActive: boolean }) {
  return (
    <Tip label="My Day">
      <NavLink
        to={{ pathname: '/my-day', search }}
        data-view="todo"
        viewTransition
        aria-label="My Day"
        // **A string, and `isActive` comes in as a prop — because a *function*
        // className cannot survive being wrapped in a `Tip`.**
        //
        // `Tip` renders a Radix `Tooltip.Trigger asChild`, which clones this
        // element and merges its own props in. That merge joins the two
        // `className` values as strings, so a function arrives at the DOM as its
        // own source code: the class attribute became
        // `({isActive:e})=>uc(\`plan-my-day tap flex …\`,e?\`active border-ink …\`:\`border-border …\`)`.
        //
        // The failure is quiet and it is *nearly* invisible, which is why it sat
        // here through a whole sitting:
        //  - whitespace-separated fragments of that source are still class
        //    tokens, so `tap`, `flex`, `h-9`, `rounded-lg` and friends all
        //    applied — the button looked right;
        //  - **both** branches applied at once, so the styling never changed
        //    between active and inactive. `bg-surface` simply wins over `bg-ink`
        //    on cascade order and always did;
        //  - `plan-my-day` was never a class at all — the token was
        //    `` `plan-my-day `` with the template literal's backtick on it;
        //  - and `active` *was* present, by luck: React Router appends it when
        //    `className` is a string, which is what this had accidentally become.
        //    That is the only reason `topbar-responsive`, which asks which view
        //    you are on through `.active`, kept passing.
        //
        // The children function is unaffected — the star did fill correctly —
        // which made the symptom "the star knows, the pill does not".
        // **The white chip, not black.** This was `border-ink bg-ink text-bg`,
        // which made the strongest "selected" statement on the bar belong to
        // the control that is *least* about where you are — and it said it in a
        // different language from the view switch beside it, which by then had
        // given its black up. Abhishek asked for one language; `.white-chip` is
        // it.
        //
        // The fill is nearly the same as the off state's `bg-surface` in light,
        // and deliberately: what separates them is the lift, the rim, the step
        // from `ink-muted` to full ink, and the star filling — four signals, no
        // hue, same as everywhere else on this bar.
        className={cn(
          'plan-my-day tap flex h-9 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-sm font-semibold no-underline transition-colors duration-fast ease-out-soft',
          isActive
            ? 'active white-chip text-ink'
            : 'border-border bg-surface text-ink-muted hover:border-border-strong hover:text-ink',
        )}
      >
        {/* Filled when you are on it. A star that only ever outlines is a
            decoration; one that fills is a state. */}
        <Star className={cn('size-4 shrink-0', isActive && 'fill-current')} aria-hidden />
        <span className="hidden phone:inline">My Day</span>
      </NavLink>
    </Tip>
  );
}
